import React from 'react';
import { MessageCircle, Clock, CheckCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { cn } from '../utils/cn';

import { useLanguage } from "../context/LanguageProvider";

export const ReclamationsCard = ({ role, roles, reclamation, onOpenChat, currentUser }) => {
  const { t } = useLanguage();
  const { order, messages } = reclamation;
  
  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  
  const unreadCount = messages.filter(m => {
    if (m.status !== 'notseen') return false;
    if (currentUser.role === 0) {
      return [1, 2, 3].includes(m.user.role);
    }
    return m.user.role === 0;
  }).length;

  const getOverallStatus = () => {
    if (!latestMessage) return 'pending';
    
    // Check if latest message is seen
    if (latestMessage.status === 'seen') return 'seen';
    
    return 'notseen';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'notseen': return <Clock className="h-3.5 w-3.5" />;
      case 'seen': return <CheckCheck className="h-3.5 w-3.5" />;
      default: return <Clock className="h-3.5 w-3.5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'notseen': return 'bg-amber-500 text-amber-100 font-[600]';
      case 'seen': return 'bg-green-600 text-green-100';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Just now';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const status = getOverallStatus();

  return (
    <Card className="shadow-lg bg-white transition-shadow duration-200 border-y-0 border-r-0 border-l-4 border-l-[var(--color-primary-500)] hover:shadow-xl cursor-pointer">
      <CardHeader className="pb-1">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-sm font-semibold dark:text-white">
              {t('reclamations.order_label')} #{order.code}
            </CardTitle>
            <p className="text-xs text-gray-600">{t('reclamations.client_label')}: {order.client?.name} - {order.client?.city}</p>
          </div>
          <div className="text-right flex items-center gap-1 flex-wrap justify-end">
            <div className={`text-[10px] flex items-center gap-1 rounded-full px-1.5 py-0.5 ${getStatusColor(status)} `}>
              {getStatusIcon(status)}
              <span className="capitalize">{status.replace('_', ' ')}</span>
            </div>
            {unreadCount > 0 && (
              <div className="bg-red-500 text-white text-[11px] px-2 py-0.5 rounded-full">
                {unreadCount}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 ">
        <div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
          <p className="font-medium">{t('reclamations.total_label')}: {parseFloat(order.total).toFixed(2)} MAD</p>
          <p className="text-[11px] text-gray-500 mt-1">
            {t('reclamations.created_at')}: {formatDate(order.created_at)}
          </p>
          {latestMessage && (
            <div className="">
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-gray-600 flex items-center">
                  {t('reclamations.last_message')}
                </span>
                <span className="text-[11px] text-gray-500 ">
                  {formatTimeAgo(latestMessage.created_at)}
                </span>
              </div>
            </div>
          )}
        </div>
                        
        
        {messages.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 text-center">
              {t('reclamations.no_messages')}
            </p>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {t('reclamations.message_count', { count: messages.length })}
          </span>
          {
            role === 0 || role === 1 || role === 3 || (role === 2 && roles === 1) ?
              <button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenChat(reclamation);
                }}
                className="border border-[var(--color-primary-500)] flex gap-0.5 py-1 rounded-md px-1.5 bg-[var(--color-primary-500)] text-white text-xs cursor-pointer hover:bg-[var(--color-primary-400)] hover:border-[var(--color-primary-500)] relative"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {t('reclamations.open')}
              </button>
              :''
          }
        </div>
      </CardContent>
    </Card>
  );
};